import React, { useState } from 'react';
import { healthAPI } from '../api';
import './UserContextPage.css';

export const HealthAssistantPage: React.FC = () => {
    const [chatInput, setChatInput] = useState('');
    const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'ai', text: string }[]>([]);
    const [chatLoading, setChatLoading] = useState(false);

    const handleChatSubmit = async (text: string = chatInput) => {
        if (!text.trim()) return;

        const userMsg = text;
        setChatInput('');
        setChatHistory(prev => [...prev, { role: 'user', text: userMsg }]);
        setChatLoading(true);

        try {
            const res = await healthAPI.chatWithAssistant(userMsg);
            setChatHistory(prev => [...prev, { role: 'ai', text: res.response }]);
        } catch (e) {
            console.error(e);
            setChatHistory(prev => [...prev, { role: 'ai', text: "Sorry, I encountered an error. Please try again." }]);
        } finally {
            setChatLoading(false);
        }
    };

    return (
        <div className="user-context-page">
            <header className="page-header">
                <h2>💬 Health Assistant</h2>
                <div className="header-actions">
                    {/* Actions if needed */}
                </div>
            </header>

            <div className="profile-section chat-section">
                <p className="section-help">
                    Ask questions about your health data, get personalized snack recommendations, or review your health patterns.
                </p>

                <div className="chat-interface">
                    <div className="chat-history">
                        {chatHistory.length === 0 && (
                            <div className="empty-chat-state">
                                Ask me anything! e.g., "What should I eat?" or "How were my symptoms last week?"
                            </div>
                        )}
                        {chatHistory.map((msg, idx) => (
                            <div key={idx} className={`chat-message ${msg.role}`}>
                                <div className="message-bubble">{msg.text}</div>
                            </div>
                        ))}
                        {chatLoading && (
                            <div className="chat-message ai">
                                <div className="message-bubble typing">Thinking...</div>
                            </div>
                        )}
                    </div>

                    <div className="quick-prompts">
                        <button onClick={() => handleChatSubmit("What is a good snack option right now?")}>🍪 Snack Idea</button>
                        <button onClick={() => handleChatSubmit("Is it a good idea to eat pizza?")}>🍕 Pizza?</button>
                        <button onClick={() => handleChatSubmit("How is my hydration today?")}>💧 Hydration Check</button>
                    </div>

                    <div className="chat-input-area">
                        <input
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
                            placeholder="Ask a question..."
                            disabled={chatLoading}
                        />
                        <button onClick={() => handleChatSubmit()} disabled={!chatInput.trim() || chatLoading}>
                            Send
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .profile-section {
                    background: white;
                    padding: 20px;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    margin-bottom: 20px;
                }
                .section-help {
                    color: #666;
                    font-size: 0.9em;
                    margin-bottom: 15px;
                    line-height: 1.4;
                }

                /* Chat Styles */
                .chat-interface {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }
                .chat-history {
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 15px;
                    height: 400px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    border: 1px solid #eee;
                }
                .empty-chat-state {
                    color: #999;
                    text-align: center;
                    margin-top: 150px;
                    font-style: italic;
                }
                .chat-message {
                    display: flex;
                }
                .chat-message.user {
                    justify-content: flex-end;
                }
                .chat-message.ai {
                    justify-content: flex-start;
                }
                .message-bubble {
                    max-width: 80%;
                    padding: 10px 15px;
                    border-radius: 12px;
                    font-size: 0.95em;
                    line-height: 1.4;
                }
                .chat-message.user .message-bubble {
                    background: #007bff;
                    color: white;
                    border-bottom-right-radius: 2px;
                }
                .chat-message.ai .message-bubble {
                    background: #e9ecef;
                    color: #333;
                    border-bottom-left-radius: 2px;
                    white-space: pre-wrap;
                }
                .message-bubble.typing {
                    font-style: italic;
                    color: #666;
                }
                .quick-prompts {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                .quick-prompts button {
                    background: #fff;
                    border: 1px solid #ddd;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 0.85em;
                    cursor: pointer;
                    transition: all 0.2s;
                    color: #555;
                }
                .quick-prompts button:hover {
                    background: #f0f0f0;
                    border-color: #ccc;
                    transform: translateY(-1px);
                }
                .chat-input-area {
                    display: flex;
                    gap: 10px;
                }
                .chat-input-area input {
                    flex: 1;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 0.95em;
                }
                .chat-input-area input:focus {
                    outline: none;
                    border-color: #007bff;
                }
                .chat-input-area button {
                    padding: 0 20px;
                    background: #007bff;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .chat-input-area button:disabled {
                    background: #ccc;
                }
            `}</style>
        </div>
    );
};
